import type { Filter } from '@/app/common/filters/core/filtersTypes.ts';
import { filterOptions, isOptionSelected } from '@/app/common/filters/core/filterOptionSelection.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { NO_STAR_RATING_VALUE } from '@/app/common/filters/core/starsValues.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';

const isConsecutive = (numbers: number[]) => numbers.every((value, i) => i === 0 || numbers[i - 1] + 1 === value);

// "4" | "4 to 5" | "2, 4 or 5" — never the word "stars", so it is safe in DE too.
export const starsNumbersText = (numbers: number[], t: TranslationFunction): string => {
  if (!numbers.length) return '';
  if (numbers.length === 1) return `${numbers[0]}`;

  const first = numbers[0];
  const last = numbers[numbers.length - 1];

  if (isConsecutive(numbers)) return t('vacation.oss.filters.stars.range', { from: `${first}`, to: `${last}` });

  const list = numbers.slice(0, -1).join(', ');
  return t('vacation.oss.filters.stars.list_or', { list, last: `${last}` });
};

export const sortedStarsNumbers = (values: string[]): number[] =>
  values
    .map((value) => parseInt(value, 10))
    .filter((value) => !Number.isNaN(value))
    .sort((a, b) => a - b);

export type StarsText = {
  text: string;
  bareNumbers: boolean;
};

// DE leaves the wrapper key untranslated, so the text collapses to bare numbers there.
export const starsWrappedText = (numbers: number[], t: TranslationFunction): StarsText => {
  const bare = starsNumbersText(numbers, t);
  const wrapped =
    numbers.length === 1 && numbers[0] === 1
      ? t('vacation.oss.filters.stars.placeholder_singular') || bare
      : t('vacation.oss.filters.stars.placeholder', { number: bare }) || bare;

  return { text: wrapped, bareNumbers: wrapped === bare };
};

export type StarsSelection = {
  /** Selected star categories, ascending. Excludes "no star rating". */
  numbers: number[];
  unrated: boolean;
};

export const starsSelectionOf = (filters: Filter[]): StarsSelection => {
  const starsFilter = filters.find((filter) => filter.name === FILTER_NAME.STARS);
  const selected = starsFilter ? filterOptions(starsFilter).filter(isOptionSelected) : [];

  return {
    numbers: sortedStarsNumbers(
      selected.filter((option) => option.value !== NO_STAR_RATING_VALUE).map((option) => option.value)
    ),
    unrated: selected.some((option) => option.value === NO_STAR_RATING_VALUE),
  };
};

export type StarsSelectionText = StarsText & { suffix: string };

export const starsSelectionText = (
  { numbers, unrated }: StarsSelection,
  t: TranslationFunction
): StarsSelectionText => {
  if (!numbers.length)
    return { text: unrated ? t('vacation.oss.filters.stars.no_rating') : '', bareNumbers: false, suffix: '' };

  return { ...starsWrappedText(numbers, t), suffix: unrated ? ' +1' : '' };
};
